import React, { useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Check, Loader2, MailCheck, RefreshCw } from "lucide-react";
import { toast } from "sonner";

/**
 * iter-121 Sprint 2 — Email OTP verification card for the Onboarding page.
 *
 * Auto-fetches the user's email_verified status. When false, renders a
 * 6-digit input + "Resend" link. On success, calls `onVerified()` so the
 * parent can refresh the profile-completion chip.
 *
 * Props:
 *   email        — user.email (used for both /send and /verify endpoints)
 *   verified     — bool, current email_verified status
 *   demoCodeHint — optional 6-digit string surfaced when SMTP is not configured
 *   onVerified() — callback after success.
 */
export default function EmailOtpCard({ email, verified, demoCodeHint, onVerified }) {
    const [code, setCode] = useState("");
    const [sending, setSending] = useState(false);
    const [verifying, setVerifying] = useState(false);
    const [cooldownSec, setCooldownSec] = useState(0);
    const [hint, setHint] = useState(demoCodeHint || "");

    useEffect(() => { setHint(demoCodeHint || ""); }, [demoCodeHint]);

    useEffect(() => {
        if (cooldownSec <= 0) return;
        const t = setInterval(() => setCooldownSec((s) => Math.max(0, s - 1)), 1000);
        return () => clearInterval(t);
    }, [cooldownSec]);

    if (verified) {
        return (
            <div className="flex items-center gap-2 px-4 py-3 border border-emerald-500/30 bg-emerald-500/5 rounded-sm text-[12px] text-emerald-700 dark:text-emerald-400"
                 data-testid="otp-verified-banner">
                <Check className="h-4 w-4" />
                <span><span className="font-medium">{email}</span> is verified.</span>
            </div>
        );
    }

    const resend = async () => {
        setSending(true);
        try {
            const r = await api.post("/auth/signup/verify-email/send", { email });
            if (r.data?.demo_code) {
                setHint(r.data.demo_code);
                toast.info(`Demo mode — code: ${r.data.demo_code}`, { description: "SMTP not configured; using surfaced code." });
            } else {
                toast.success("Verification code sent", { description: `Check your inbox at ${email}.` });
            }
            setCooldownSec(60);
        } catch (err) {
            const detail = err?.response?.data?.detail || "Could not send code";
            if (err?.response?.status === 429) {
                // Surface the cooldown server-side messaging
                setCooldownSec(60);
            }
            toast.error(detail);
        } finally {
            setSending(false);
        }
    };

    const verify = async () => {
        const clean = (code || "").replace(/\D/g, "");
        if (clean.length < 4) {
            toast.error("Enter the 6-digit code first.");
            return;
        }
        setVerifying(true);
        try {
            await api.post("/auth/signup/verify-email", { email, code: clean });
            toast.success("Email verified ✓", { description: "Thanks — that unlocks a few perks." });
            setCode("");
            onVerified && onVerified();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Verification failed");
        } finally {
            setVerifying(false);
        }
    };

    return (
        <div className="border border-border rounded-sm bg-secondary/20 p-4" data-testid="otp-card">
            <div className="flex items-start gap-2 mb-3">
                <MailCheck className="h-4 w-4 text-primary mt-0.5" />
                <div className="flex-1">
                    <div className="text-sm font-medium">Verify your email</div>
                    <div className="text-[11px] text-muted-foreground leading-snug">
                        We sent a 6-digit code to <span className="font-mono">{email}</span>. Enter it below to unlock full workspace features.
                    </div>
                </div>
            </div>
            {hint && (
                <div className="mb-2 text-[10px] font-mono text-muted-foreground p-2 bg-background border border-dashed border-border rounded-sm" data-testid="otp-demo-hint">
                    SMTP off · demo code: <span className="text-primary">{hint}</span>
                </div>
            )}
            <div className="flex gap-2">
                <input
                    inputMode="numeric"
                    maxLength={6}
                    pattern="\d{4,6}"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                    onKeyDown={(e) => e.key === "Enter" && verify()}
                    placeholder="123456"
                    className="flex-1 h-10 px-3 bg-background border border-border rounded-sm text-sm font-mono tracking-[0.4em] text-center focus:outline-none focus:ring-1 focus:ring-primary"
                    data-testid="otp-code-input"
                />
                <Button onClick={verify} disabled={verifying || code.length < 4} className="rounded-sm" data-testid="otp-verify-button">
                    {verifying ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Verifying</> : "Verify"}
                </Button>
            </div>
            <div className="flex items-center justify-between mt-2">
                <button
                    type="button"
                    onClick={resend}
                    disabled={sending || cooldownSec > 0}
                    className="text-[11px] text-primary hover:underline disabled:text-muted-foreground disabled:no-underline flex items-center gap-1"
                    data-testid="otp-resend-button"
                >
                    <RefreshCw className="h-3 w-3" />
                    {cooldownSec > 0 ? `Resend in ${cooldownSec}s` : "Resend code"}
                </button>
                <span className="text-[10px] text-muted-foreground">Code expires after 10 minutes.</span>
            </div>
        </div>
    );
}
